import type { ReactNode } from "react";

export function Card({
  children,
  className = "",
  title,
  actions,
}: {
  children: ReactNode;
  className?: string;
  title?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className={`card p-4 ${className}`}>
      {(title || actions) && (
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="text-sm font-semibold text-slate-100">{title}</div>
          <div className="flex items-center gap-2">{actions}</div>
        </div>
      )}
      {children}
    </div>
  );
}

export function Badge({
  tone = "muted",
  children,
}: {
  tone?: "ok" | "warn" | "bad" | "info" | "muted";
  children: ReactNode;
}) {
  const cls =
    tone === "ok"
      ? "badge-ok"
      : tone === "warn"
      ? "badge-warn"
      : tone === "bad"
      ? "badge-bad"
      : tone === "info"
      ? "badge-info"
      : "badge-muted";
  return <span className={cls}>{children}</span>;
}

export function Kpi({
  label,
  value,
  tone = "info",
  hint,
}: {
  label: string;
  value: number | string;
  tone?: "ok" | "warn" | "bad" | "info" | "muted";
  hint?: string;
}) {
  return (
    <div className="card p-4">
      <div className="text-xs uppercase tracking-wide text-slate-400">
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <div className="text-2xl font-bold text-slate-50">{value}</div>
        {hint ? <Badge tone={tone}>{hint}</Badge> : null}
      </div>
    </div>
  );
}

export function Empty({
  title = "Nothing here yet.",
  hint,
}: {
  title?: string;
  hint?: string;
}) {
  return (
    <div className="card p-6 text-center text-slate-400">
      <div className="text-sm font-semibold text-slate-200">{title}</div>
      {hint && <div className="mt-1 text-xs">{hint}</div>}
    </div>
  );
}

export function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-4 w-full rounded bg-panel2 animate-pulse"
        />
      ))}
    </div>
  );
}

export function ReviewBanner() {
  return (
    <div className="review-banner">
      Draft — requires physician review.
    </div>
  );
}

export function ErrorBox({ error }: { error: unknown }) {
  const msg =
    error instanceof Error ? error.message : "Something went wrong.";
  return (
    <div className="rounded-md border border-red-700/60 bg-red-900/30 px-3 py-2 text-sm text-red-200">
      {msg}
    </div>
  );
}
